import React, { useEffect, useState } from "react";

import AppLayout from "components/ui/AppLayout";
import KPICard from "./components/KPICard";
import RevenueCustomerChart from "./components/RevenueCustomerChart";
import SatisfactionGauge from "./components/SatisfactionGauge";
import TopRegionsTable from "./components/TopRegionsTable";
import ForecastTrendChart from "./components/ForecastTrendChart";
import Button from "components/ui/Button";
import Icon from "components/AppIcon";
import DataUnavailable from "components/ui/DataUnavailable";
import { reportsApi, formatLastDataUtc } from "api/reports";
import { getCurrentUser } from "api/client";

export default function ExecutiveOverview() {
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const currentUser = getCurrentUser();
  const isManager = currentUser?.role === "Owner" || currentUser?.role === "Manager";
  const [widgets, setWidgets] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    const loadWidgets = async () => {
      try {
        const data = await reportsApi.getExecutive();
        setWidgets(data);
        setUnavailable(false);
        setLoadError("");
      } catch (error) {
        if (error?.code === "NOT_AVAILABLE") {
          setUnavailable(true);
        } else {
          setLoadError(error?.message || "Unable to load live executive data.");
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadWidgets();
  }, []);

  const retry = () => {
    setIsLoading(true);
    setUnavailable(false);
    setLoadError("");
    reportsApi.getExecutive()
      .then((data) => {
        setWidgets(data);
        setIsLoading(false);
      })
      .catch((error) => {
        if (error?.code === "NOT_AVAILABLE") setUnavailable(true);
        else setLoadError(error?.message || "Unable to load live executive data.");
        setIsLoading(false);
      });
  };

  const kpis = widgets?.kpis || [];

  const handleExport = (dataset) => {
    setExportMenuOpen(false);
    reportsApi.exportDataset(dataset).catch((error) => {
      setLoadError(error?.message || "Export failed.");
    });
  };

  return (
    <AppLayout>
      <div
        className="min-h-screen"
        style={{ background: "var(--color-background)" }}
      >
        <main className="px-4 md:px-6 lg:px-8 py-6 max-w-screen-2xl mx-auto">
          {/* Page Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h1
                className="font-heading font-bold text-2xl md:text-3xl"
                style={{ color: "var(--color-foreground)" }}
              >
                Executive Overview
              </h1>
              <p className="font-caption text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>
                Strategic CRM metrics — live from the CRM (as of {formatLastDataUtc(widgets?.lastDataAtUtc)})
              </p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              {/* Export Button — CSV export endpoint is Owner/Manager only (Sprint 15 QA). */}
              {isManager && (
              <div className="relative">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Download"
                  iconPosition="left"
                  iconSize={14}
                  onClick={() => setExportMenuOpen((p) => !p)}
                >
                  Export
                </Button>
                {exportMenuOpen && (
                  <div
                    className="absolute right-0 top-full mt-1 rounded-md border py-1 z-50"
                    style={{
                      background: "var(--color-popover)",
                      borderColor: "var(--color-border)",
                      boxShadow: "var(--shadow-lg)",
                      minWidth: "200px",
                    }}
                  >
                    <button
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm transition-colors"
                      style={{ color: "var(--color-foreground)", fontFamily: "var(--font-caption)" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--color-muted)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      onClick={() => handleExport("leads")}
                    >
                      <Icon name="FileText" size={14} color="var(--color-error)" />
                      Leads (CSV)
                    </button>
                    <button
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm transition-colors"
                      style={{ color: "var(--color-foreground)", fontFamily: "var(--font-caption)" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--color-muted)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      onClick={() => handleExport("deals")}
                    >
                      <Icon name="Presentation" size={14} color="var(--color-primary)" />
                      Deals (CSV)
                    </button>
                    <button
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm transition-colors"
                      style={{ color: "var(--color-foreground)", fontFamily: "var(--font-caption)" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--color-muted)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      onClick={() => handleExport("contacts")}
                    >
                      <Icon name="Users" size={14} color="var(--color-success)" />
                      Contacts (CSV)
                    </button>
                  </div>
                )}
              </div>
              )}
            </div>
          </div>

          {/* Refresh */}
          <div className="mb-6 flex items-center justify-between">
            <Button variant="outline" size="sm" iconName="RefreshCw" iconPosition="left" iconSize={14} onClick={retry} disabled={isLoading}>
              {isLoading ? 'Refreshing...' : 'Refresh'}
            </Button>
            <span className="font-caption text-xs" style={{ color: 'var(--color-text-secondary)' }}>
              Data refreshes on page load
            </span>
          </div>

          {!isLoading && unavailable && (
            <div className="mb-6">
              <DataUnavailable feature="Executive Overview" onRetry={retry} />
            </div>
          )}

          {!unavailable && (
            <>
          {/* KPI Cards Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-6">
            {isLoading && (
              <div className="sm:col-span-2 lg:col-span-4 rounded-lg border px-4 py-5" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
                <p className="font-caption text-sm" style={{ color: "var(--color-text-secondary)" }}>Loading live executive data...</p>
              </div>
            )}
            {!isLoading && loadError && (
              <div className="sm:col-span-2 lg:col-span-4 rounded-lg border px-4 py-5" style={{ borderColor: "var(--color-error)", background: "var(--color-card)" }}>
                <p className="font-caption text-sm" style={{ color: "var(--color-error)" }}>{loadError}</p>
              </div>
            )}
            {kpis?.map((kpi) => (
              <KPICard
                key={kpi?.title}
                {...kpi}
                change={kpi?.change ?? kpi?.changePercent}
              />
            ))}
          </div>

          {/* Main Content: Chart + Sidebar */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-5 mb-6">
            {/* Revenue & Customer Chart — 8 cols */}
            <div className="lg:col-span-8">
              <RevenueCustomerChart data={widgets?.revenueCustomerTrend} />
            </div>

            {/* Right Sidebar — 4 cols */}
            <div className="lg:col-span-4 flex flex-col gap-4 md:gap-5">
              {widgets?.satisfaction ? (
                <SatisfactionGauge data={widgets.satisfaction} />
              ) : (
                <div
                  className="rounded-xl border px-4 py-6 text-center"
                  style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}
                >
                  <p className="font-caption text-xs" style={{ color: "var(--color-text-secondary)" }}>
                    Customer satisfaction — no data source yet
                  </p>
                </div>
              )}
              <TopRegionsTable regions={widgets?.topRegions} />
            </div>
          </div>

          {/* Forecast Trend — Full Width */}
          <div className="mb-6">
            <ForecastTrendChart data={widgets?.forecastTrend} />
          </div>
          </>
          )}

          {/* Footer */}
          <div
            className="flex flex-col sm:flex-row items-center justify-between gap-2 pt-4 border-t"
            style={{ borderColor: "var(--color-border)" }}
          >
            <p className="font-caption text-xs" style={{ color: "var(--color-text-secondary)" }}>
              &copy; {new Date()?.getFullYear()} CRMAnalytics. All rights reserved. Data as of {formatLastDataUtc(widgets?.lastDataAtUtc)}.
            </p>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <Icon name="Shield" size={12} color="var(--color-success)" />
                <span className="font-caption text-xs" style={{ color: "var(--color-text-secondary)" }}>
                  SSL Secured
                </span>
              </div>
            </div>
          </div>
        </main>
      </div>
    </AppLayout>
  );
}